import { collection, doc, type Reference, subCollection } from "@doitintl/models-types";

@collection("cloudflowEngine")
export class CloudflowEngine {
  docs?: {
    integrations: Integrations;
    triggers: EventTrigger;
  };
}

@doc("integrations")
export class Integrations {
  subCollections?: {
    apis: ApiDescriptor;
  };
}

@doc("triggers")
export class EventTrigger {
  subCollections?: {
    sources: EventSource;
  };
}

@subCollection("sources")
export class EventSource {
  docs?: {
    systemEvents: SystemEvent;
  };
}

@doc("systemEvents")
export class SystemEvent {
  sourceName!: string;

  description?: string;

  subCollections?: {
    events: CloudflowTriggerEvent;
    models: SystemEventsModelDescriptor;
  };
}

@subCollection("events")
export class CloudflowTriggerEvent {
  name!: string;

  latestVersionRef!: Reference<CloudflowTriggerEventVersion>;

  subCollections?: {
    versions: CloudflowTriggerEventVersion;
  };
}

@doc("versions")
export class CloudflowTriggerEventVersion {
  versionName!: string;

  modelRef!: Reference<ApiServiceModelDescriptor>;
}

@subCollection("models")
export class SystemEventsModelDescriptor {
  subCollections?: {
    versions: UnwrappedApiServiceModelDescriptor;
  };
}

@subCollection("apis")
export class ApiDescriptor {
  providerName!: string;

  providerShortName!: string;

  description?: string;

  subCollections?: {
    services: ApiServiceDescriptor;
  };
}

@subCollection("services")
export class ApiServiceDescriptor {
  serviceName!: string;

  permissionsNamespace?: string;

  iconURL?: string;

  documentation?: string;

  documentationLink?: string;

  description?: string;

  latestVersion!: string;

  subCollections?: {
    versions: ApiServiceVersionDescriptor;
  };
}

@subCollection("versions")
export class ApiServiceVersionDescriptor {
  versionId!: string;

  name?: string;

  subCollections?: {
    operations: ApiServiceOperationDescriptor;
    models: ApiServiceModelDescriptor;
    waiters: ApiServiceWaiterDescriptor;
  };
}

export enum ApiServiceOperationType {
  RPC = "rpc",
  HTTP = "http",
}

export class ApiServiceBaseOperationDescriptor<
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  OperationType extends ApiServiceOperationType = ApiServiceOperationType,
> {
  type!: OperationType;

  providerId!: string;

  providerName!: string;

  serviceId!: string;

  serviceName!: string;

  version!: string;

  operationName!: string;

  documentation?: string;

  description?: string;

  inputModel?: string;

  outputModel?: string;

  permissions?: string[];

  parameters?: StructureApiServiceModelDescriptor<Member>;

  pagination?: {
    inputMember?: string;
    outputMember?: string;
    nextPageFlag?: string;
    pageSize?: string;
  };
}

@subCollection("waiters")
export class ApiServiceWaiterDescriptor {
  documentation?: string;

  inputModel?: Reference<ApiServiceModelDescriptor>;

  operationModel!: Reference<ApiServiceOperationDescriptor>;

  minDelay!: number;

  maxDelay?: number;

  maxWaitTime?: number;
}

@subCollection("operations")
export class ApiServiceRpcOperationDescriptor extends ApiServiceBaseOperationDescriptor<ApiServiceOperationType.RPC> {
  commandName!: string;

  moduleName!: string;
}

@subCollection("operations")
export class ApiServiceHttpOperationDescriptor extends ApiServiceBaseOperationDescriptor<ApiServiceOperationType.HTTP> {
  headers?: Headers;

  payload?: unknown;

  resource?: string;

  scopes?: string[];

  httpRequestType?: HttpRequestType;
}

export type ApiServiceOperationDescriptor = ApiServiceRpcOperationDescriptor | ApiServiceHttpOperationDescriptor;

export type MemberReference = {
  modelId: string;
  documentation?: string;
  label?: string;
};

export type Member = {
  model: UnwrappedApiServiceModelDescriptor;
  documentation?: string;
  label?: string;
};

export enum HttpRequestType {
  GET = "get",
  PUT = "put",
  POST = "post",
  DELETE = "delete",
  OPTIONS = "options",
  HEAD = "head",
  PATCH = "patch",
}

export enum ModelType {
  INTEGER = "integer",
  BOOLEAN = "boolean",
  FLOAT = "float",
  STRING = "string",
  LIST = "list",
  STRUCTURE = "structure",
  TIMESTAMP = "timestamp",
  MAP = "map",
  UNION = "union",
}

// These types are to be used with the cloudflow openapi extension `x-cloudflow-format`
// multiline - renders a string input as a text area
// noReference - disables referencing of other nodes
// multiReference - renders a refernceable text area
export const OpenAPICloudflowExtensionFormats = ["multiline", "noReference", "multiReference"] as const;
export type OpenAPICloudflowExtensionFormat = (typeof OpenAPICloudflowExtensionFormats)[number];

export function isOpenAPIExtensionFormat(value: string): value is OpenAPICloudflowExtensionFormat {
  return OpenAPICloudflowExtensionFormats.includes(value as OpenAPICloudflowExtensionFormat);
}

export const OPEN_API_FORMAT_EXTENSION_KEY = "x-cloudflow-format";
export const OPEN_API_LABELS_EXTENSION_KEY = "x-cloudflow-labels";

export class BaseApiServiceModelDescriptor {
  documentation?: string;

  example?: string;

  location?: `path` | `query` | `header` | `cookie`;
}

@subCollection("models")
export class IntegerApiServiceModelDescriptor extends BaseApiServiceModelDescriptor {
  type!: ModelType.INTEGER;

  min?: number;

  max?: number;
}

@subCollection("models")
export class FloatApiServiceModelDescriptor extends BaseApiServiceModelDescriptor {
  type!: ModelType.FLOAT;

  min?: number;

  max?: number;
}

@subCollection("models")
export class BooleanApiServiceModelDescriptor extends BaseApiServiceModelDescriptor {
  type!: ModelType.BOOLEAN;
}

@subCollection("models")
export class StringApiServiceModelDescriptor extends BaseApiServiceModelDescriptor {
  type!: ModelType.STRING;

  minLength?: number;

  maxLength?: number;

  pattern?: string;

  enum?: string[];

  enumDescriptions?: string[];

  sensitive?: true;

  multiline?: true;

  referenceFieldType?: Extract<OpenAPICloudflowExtensionFormat, "noReference" | "multiReference">;
}

@subCollection("models")
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export class ListApiServiceModelDescriptor<MemberType> extends BaseApiServiceModelDescriptor {
  type!: ModelType.LIST;

  member!: MemberType;

  memberName?: string;

  min?: number;

  max?: number;
}

@subCollection("models")
export class StructureApiServiceModelDescriptor<MemberType> extends BaseApiServiceModelDescriptor {
  type!: ModelType.STRUCTURE;

  members!: Record<string, MemberType>;

  requiredMembers?: string[];
}

@subCollection("models")
export class TimestampApiServiceModelDescriptor extends BaseApiServiceModelDescriptor {
  type!: ModelType.TIMESTAMP;

  timestampFormat?: string;
}

@subCollection("models")
export class MapApiServiceModelDescriptor<
  MemberType extends Member | MemberReference,
> extends BaseApiServiceModelDescriptor {
  type!: ModelType.MAP;

  keyMember!: MemberType;

  keyMemberName!: string;

  valueMember!: MemberType;

  valueMemberName!: string;

  min?: number;

  max?: number;

  sensitive?: true;
}

@subCollection("models")
export class UnionApiServiceModelDescriptor extends BaseApiServiceModelDescriptor {
  type!: ModelType.UNION;

  members!: UnwrappedApiServiceModelDescriptor[];
}

export type ApiServiceModelDescriptor<MemberType extends Member | MemberReference = Member | MemberReference> =
  | IntegerApiServiceModelDescriptor
  | FloatApiServiceModelDescriptor
  | BooleanApiServiceModelDescriptor
  | StringApiServiceModelDescriptor
  | ListApiServiceModelDescriptor<MemberType>
  | StructureApiServiceModelDescriptor<MemberType>
  | TimestampApiServiceModelDescriptor
  | MapApiServiceModelDescriptor<MemberType>
  | UnionApiServiceModelDescriptor;

export type HttpPathParameterModelDescriptor =
  | IntegerApiServiceModelDescriptor
  | FloatApiServiceModelDescriptor
  | StringApiServiceModelDescriptor;

export type UnwrappedApiServiceModelDescriptor = ApiServiceModelDescriptor<Member>;
